/**
 * Integrytas Group — Common Security Module (CSM)
 * storage_principal — per-user scoping for browser-persisted shell state.
 *
 * WHY: open tabs, the active tab, column sort and menu expansion were stored
 * under fixed keys (`csm.openTabs`, `csm.activeTab`, …). Browser storage
 * outlives a sign-out, so signing in as someone else inherited the previous
 * user's workspace — an admin's Users and Roles tabs would still be open for a
 * buyer who has no access to either page.
 *
 * The principal is the EFFECTIVE user, so mimic behaves as though the mimicked
 * user is at the keyboard: starting a mimic swaps the tab strip to that user's
 * workspace and ending it restores the admin's, matching the permissions and
 * menu that already follow `effective_user`. Forensic identity is unaffected —
 * writes still stamp the real admin server-side (§3, §12).
 *
 * This is a module rather than a hook because the storage sites are lazy
 * useState initialisers and plain helpers, which cannot consume context.
 * `use_favorites` already scopes this way (`csm.favorites.<user_id>`); this
 * generalises the same idea to the rest of the shell.
 */

/** Keys written before scoping existed, removed once so they cannot linger. */
const LEGACY_KEYS = ['csm.openTabs', 'csm.activeTab'] as const;

let principal: number | null = null;

/**
 * Point subsequent scoped reads/writes at a user. App calls this during render,
 * before anything reads storage, whenever the session resolves, changes (mimic
 * start/stop) or clears on sign-out. Deliberately plain module state with no
 * subscribers: the shell re-hydrates by being remounted on a `key` of the same
 * principal, so there is no second notification path to keep in step.
 */
export function setStoragePrincipal(user_id: number | null): void {
  principal = user_id;
}

export function getStoragePrincipal(): number | null {
  return principal;
}

/**
 * Namespace a storage key to the current principal.
 *
 * Signed out (principal null) the base key is returned unchanged: pre-auth
 * state — the sign-in screen's own bookkeeping — belongs to nobody and must
 * not be filed under a user.
 *
 * `forPrincipal` is for callers that already hold the id and need it to be a
 * visible dependency (a React memo re-reading storage when the user changes);
 * everything else omits it and gets the module's current principal.
 */
export function scopedKey(base: string, forPrincipal: number | null = principal): string {
  return forPrincipal == null ? base : `${base}.u${forPrincipal}`;
}

/** Drop the pre-scoping keys, so one legacy workspace cannot leak to a user. */
export function purgeLegacyShellState(): void {
  for (const key of LEGACY_KEYS) {
    try { sessionStorage.removeItem(key); } catch { /* noop */ }
  }
}
